/**
 * DevMode — Criador de sites e funções com IA
 * FASE 3: AI Task Generator (Elon Router) + Task Executor (Runner v3 jobs) + Streaming logs
 */

import { useEffect, useState, useRef, useCallback } from 'preact/hooks';
import { signal } from '@preact/signals';
import { api } from '../api/client';
import { hasActivePlan, isAdmin } from '../stores/auth.store';
import { NoPlanGate } from '../components/NoPlanGate';
import { SkeletonBar, Button, Input, Select, Textarea } from '../components/ui';
import { T } from '../styles/tokens';

/* ═══ Types ═══ */
interface DevProject {
  id: string;
  name: string;
  description: string | null;
  site_url: string | null;
  expected_path: string | null;
  source_files: string | null;
  runner_id: string | null;
  status: string;
  created_at: string;
  updated_at: string;
}

interface DevTask {
  id: string;
  project_id: string;
  title: string;
  description: string | null;
  prompt: string | null;
  step_number: number;
  status: string;
  output: string | null;
  error: string | null;
  duration_ms: number | null;
  category: string | null;
  created_at: string;
  completed_at: string | null;
  scheduled_at: string | null;
  recurrence_interval: string | null;
  last_run_at: string | null;
}

interface DevJob {
  id: string;
  task_id: string;
  project_id: string;
  runner_id: string;
  command: string;
  status: string;
  stdout: string | null;
  stderr: string | null;
  exit_code: number | null;
  duration_ms: number | null;
  created_at: string;
  completed_at: string | null;
}

interface DevLog {
  id: string;
  project_id: string;
  task_id: string | null;
  type: string;
  message: string;
  data: string | null;
  created_at: string;
}

/* ═══ State ═══ */
const projects = signal<DevProject[]>([]);
const loading = signal(true);
const error = signal('');

/* ═══ Theme ═══ */
const theme = {
  bg: T.bg,
  card: T.bgInput,
  cardHover: '#151D2E',
  primary: T.primary,
  indigo: T.accent,
  cyan: T.primary,
  text: T.text,
  muted: T.textSecondary,
  dimmed: T.textMuted,
  success: T.success,
  warning: T.warning,
  error: T.danger,
  border: T.border,
  gradient: `linear-gradient(135deg, ${T.primary} 0%, #6D4CFF 100%)`,
  gradientSubtle: `linear-gradient(135deg, ${T.primary} 0%, ${T.accentBg} 100%)`,
  font: "'Manrope', system-ui, sans-serif",
};

const STATUS_MAP: Record<string, { label: string; color: string; icon: string }> = {
 draft: { label: 'Rascunho', color: theme.dimmed, icon: '' },
 planning: { label: 'Planejando', color: theme.warning, icon: '' },
 in_progress: { label: 'Em progresso', color: theme.primary, icon: '' },
 completed: { label: 'Concluído', color: theme.success, icon: '' },
 failed: { label: 'Falhou', color: theme.error, icon: '' },
};

const TASK_STATUS: Record<string, { label: string; color: string; icon: string }> = {
 pending: { label: 'Pendente', color: theme.dimmed, icon: '' },
 scheduled: { label: 'Agendado', color: theme.indigo, icon: '' },
 running: { label: 'Executando', color: theme.warning, icon: '' },
 completed: { label: 'Concluído', color: theme.success, icon: '' },
 failed: { label: 'Falhou', color: theme.error, icon: '' },
};

const RECURRENCE_OPTIONS = [
  { value: '', label: 'Sem recorrência' },
  { value: '1h', label: 'A cada 1 hora' },
  { value: '6h', label: 'A cada 6 horas' },
  { value: '12h', label: 'A cada 12 horas' },
  { value: '24h', label: 'A cada 24 horas' },
  { value: '7d', label: 'A cada 7 dias' },
  { value: '30d', label: 'A cada 30 dias' },
];

const CATEGORIES = [
 { key: 'sales', label: 'Vendas & Comércio', icon: '', color: T.success },
 { key: 'marketing', label: 'Marketing & Comunicação', icon: '', color: T.warning },
 { key: 'support', label: 'Atendimento & Suporte', icon: '', color: T.primary },
 { key: 'operations', label: 'Operações & Processos', icon: '', color: T.purple },
 { key: 'hr', label: 'Recursos Humanos', icon: '', color: T.danger },
 { key: 'financial', label: 'Financeiro & Administrativo', icon: '', color: '#14B8A6' },
 { key: 'product', label: 'Produto & Desenvolvimento', icon: '', color: T.warning },
 { key: 'analysis', label: 'Análise & Inteligência', icon: '', color: '#06B6D4' },
 { key: 'compliance', label: 'Compliance & Segurança', icon: '', color: T.danger },
] as const;

/* ═══ API ═══ */
async function fetchProjects() {
  loading.value = true;
  error.value = '';
  try {
    const res = await api.get<{ projects: DevProject[] }>('/dev-mode/projects');
    projects.value = res.projects || [];
  } catch (e: any) {
    error.value = e.message || 'Erro ao carregar projetos';
  } finally {
    loading.value = false;
  }
}

/* ═══ Pill Component ═══ */
function StatusPill({ status, map }: { status: string; map: Record<string, { label: string; color: string; icon?: string }> }) {
  const cfg = map[status] || { label: status, color: theme.dimmed, icon: '' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      color: cfg.color, background: `${cfg.color}18`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color }} />
      {cfg.label}
    </span>
  );
}

function CategoryPill({ category }: { category: string | null }) {
  if (!category) return null;
  const cat = CATEGORIES.find(c => c.key === category);
  if (!cat) return null;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 12, fontSize: 10, fontWeight: 600,
      color: cat.color, background: `${cat.color}15`,
    }}>
      {cat.icon} {cat.label}
    </span>
  );
}

/* ═══ Runner Selector Modal ═══ */
function RunnerSelector({ onSelect, onCancel }: { onSelect: (runnerId: string) => void; onCancel: () => void }) {
  const [runners, setRunners] = useState<any[]>([]);
  const [loadingR, setLoadingR] = useState(true);
  const [selectedRunnerId, setSelectedRunnerId] = useState('');

  useEffect(() => {
    (async () => {
      try {
        // Try v3 runners first, fallback to v1
        let list: any[] = [];
        try {
          const res = await api.get<any>('/runner-v3/list');
          list = res.runners || [];
        } catch (_) {}
        if (list.length === 0) {
          const res = await api.get<any>('/runners');
          list = res.runners || [];
        }
        setRunners(list);
      } catch (_) {}
      setLoadingR(false);
    })();
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, background: T.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: theme.card, borderRadius: 16, padding: 32, maxWidth: 500, width: '90%', border: `1px solid ${theme.border}` }}>
 <h3 style={{ fontSize: 18, fontWeight: 700, color: theme.text, marginBottom: 20 }}>Selecione uma Máquina</h3>

        {loadingR ? (
          <div style={{ padding: 12 }}><SkeletonBar width="60%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="40%" height={14} /></div>
        ) : runners.length === 0 ? (
          <div style={{ color: theme.error, fontSize: 14 }}>Nenhuma máquina conectada. Crie uma em Cloud Machines.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
            {runners.map((r: any) => (
              <label key={r.id} style={{
                display: 'flex', alignItems: 'center', gap: 12, padding: 12, borderRadius: 10,
                background: selectedRunnerId === r.id ? T.primaryBg : 'transparent',
                border: `1px solid ${selectedRunnerId === r.id ? T.primary : theme.border}`,
                cursor: 'pointer',
              }}>
                <input type="radio" name="runner" value={r.id} checked={selectedRunnerId === r.id}
                  onChange={(e: any) => setSelectedRunnerId(e.target.value)} style={{ cursor: 'pointer' }} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>
                    {r.name}
                    {r.status === 'online' && <span style={{ marginLeft: 8, color: theme.success, fontSize: 11 }}>● online</span>}
                    {r.status === 'offline' && <span style={{ marginLeft: 8, color: theme.dimmed, fontSize: 11 }}>● offline</span>}
                  </div>
                  <div style={{ fontSize: 12, color: theme.dimmed }}>{r.os || r.server_ip} • {r.arch || r.server_region}</div>
                </div>
              </label>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 12 }}>
          <Button onClick={() => onSelect(selectedRunnerId)} disabled={!selectedRunnerId}>
            Selecionar
          </Button>
          <Button variant="ghost" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ═══ Activity Log (streaming) ═══ */
function ActivityLog({ projectId }: { projectId: string }) {
  const [logs, setLogs] = useState<DevLog[]>([]);
  const logEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    let since = new Date(Date.now() - 3600000).toISOString(); // Last hour

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await api.get<{ logs: DevLog[] }>(`/dev-mode/projects/${projectId}/logs?since=${encodeURIComponent(since)}&limit=30`);
        const newLogs = res.logs || [];
        if (newLogs.length > 0) {
          setLogs(prev => {
            const ids = new Set(prev.map(l => l.id));
            const merged = [...prev, ...newLogs.filter(l => !ids.has(l.id))];
            return merged.slice(-100); // Keep last 100
          });
          since = newLogs[newLogs.length - 1]!.created_at;
        }
      } catch (_) {}
      if (!cancelled) setTimeout(poll, 3000);
    };

    poll();
    return () => { cancelled = true; };
  }, [projectId]);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  const typeColors: Record<string, string> = {
    info: theme.muted,
    warn: theme.warning,
    error: theme.error,
    success: theme.success,
    progress: theme.primary,
    ai: theme.indigo,
    runner: theme.cyan,
  };

  if (logs.length === 0) {
    return (
      <div style={{ padding: 16, color: theme.dimmed, fontSize: 13, textAlign: 'center' as any }}>
        Nenhum log ainda. Execute tarefas para ver atividade aqui.
      </div>
    );
  }

  return (
    <div style={{
      maxHeight: 300, overflowY: 'auto', padding: 12,
      background: T.blackAlpha30, borderRadius: 10, fontFamily: 'monospace', fontSize: 12,
    }}>
      {logs.map(log => (
        <div key={log.id} style={{ display: 'flex', gap: 8, padding: '4px 0', borderBottom: `1px solid ${T.bgCard}` }}>
          <span style={{ color: theme.dimmed, flexShrink: 0, width: 55 }}>
            {new Date(log.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </span>
          <span style={{ color: typeColors[log.type] || theme.muted, minWidth: 55, flexShrink: 0, fontWeight: 600 }}>
            [{log.type.toUpperCase()}]
          </span>
          <span style={{ color: theme.text, flex: 1 }}>{log.message}</span>
        </div>
      ))}
      <div ref={logEndRef} />
    </div>
  );
}

/* ═══ Create Project Form ═══ */
function CreateProjectForm({ onCreated, onCancel }: { onCreated: () => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [siteUrl, setSiteUrl] = useState('');
  const [sourceFiles, setSourceFiles] = useState('');
  const [expectedPath, setExpectedPath] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [showRunnerSelector, setShowRunnerSelector] = useState(false);

  const doCreate = async (runnerId: string) => {
    setSaving(true);
    setErr('');
    try {
      await api.post('/dev-mode/projects', {
        name: name.trim(),
        description: description.trim(),
        site_url: siteUrl.trim() || null,
        source_files: sourceFiles.trim() || null,
        expected_path: expectedPath.trim() || null,
        runner_id: runnerId || null,
      });
      onCreated();
    } catch (e: any) {
      setErr(e.message || 'Erro ao criar projeto');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e: Event) => {
    e.preventDefault();
    if (!description.trim()) { setErr('Descrição é obrigatória'); return; }
    if (!name.trim()) { setErr('Nome do projeto é obrigatório'); return; }
    setShowRunnerSelector(true);
  };

  const inputStyle: any = {
    width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 14,
    background: T.bgCard, border: `1px solid ${theme.border}`,
    color: theme.text, fontFamily: theme.font, outline: 'none', resize: 'vertical' as any,
    transition: 'border-color 0.15s',
  };

  const labelStyle: any = {
    display: 'block', fontSize: 12, fontWeight: 600, color: theme.muted,
    marginBottom: 6, textTransform: 'uppercase' as any, letterSpacing: '0.5px',
  };

  return (
    <form onSubmit={handleSubmit} style={{
      background: theme.card, borderRadius: 16, padding: 28,
      border: `1px solid ${theme.border}`, maxWidth: 640,
    }}>
      {showRunnerSelector && (
        <RunnerSelector
          onSelect={(runnerId) => {
            setShowRunnerSelector(false);
            doCreate(runnerId);
          }}
          onCancel={() => setShowRunnerSelector(false)}
        />
      )}
      <h3 style={{ fontSize: 18, fontWeight: 700, color: theme.text, marginBottom: 20 }}>
 Novo Projeto
      </h3>

      {err && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: T.dangerBg, color: theme.error, fontSize: 13, marginBottom: 16 }}>
          {err}
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <Input label="Nome do Projeto *" value={name} onChange={setName} placeholder="Ex: Meu SaaS" />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Descrição / O que quer construir *</label>
        <Textarea value={description} onInput={(e: any) => setDescription(e.target.value)}
          placeholder="Descreva em detalhes o que o sistema deve fazer..."
          rows={4} style={inputStyle} />
      </div>

      <div style={{ marginBottom: 16 }}>
        <Input label="Site (opcional)" value={siteUrl} onChange={setSiteUrl} placeholder="https://meusite.com" />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Arquivos do sistema (opcional)</label>
        <Textarea value={sourceFiles} onInput={(e: any) => setSourceFiles(e.target.value)}
          placeholder="Lista de arquivos relevantes, estrutura de pastas, código existente..."
          rows={3} style={inputStyle} />
      </div>

      <div style={{ marginBottom: 24 }}>
        <Input label="Caminho esperado (opcional)" value={expectedPath} onChange={setExpectedPath} placeholder="/var/www/meu-projeto" />
      </div>

      <div style={{ display: 'flex', gap: 12 }}>
        <Button type="submit" disabled={saving} loading={saving}>
          {saving ? 'Criando...' : 'Criar Projeto'}
        </Button>
        <Button variant="ghost" type="button" onClick={onCancel}>
          Cancelar
        </Button>
      </div>
    </form>
  );
}

/* ═══ New Function Form ═══ */
function NewFunctionForm({ project, onDone, onCancel }: { project: DevProject; onDone: () => void; onCancel: () => void }) {
  const [description, setDescription] = useState('');
  const [expectedItems, setExpectedItems] = useState('');
  const [category, setCategory] = useState('');
  const [generating, setGenerating] = useState(false);
  const [tasks, setTasks] = useState<DevTask[]>([]);
  const [err, setErr] = useState('');

  const handleGenerate = async (e: Event) => {
    e.preventDefault();
    if (!description.trim()) { setErr('Descrição da função é obrigatória'); return; }
    setGenerating(true);
    setErr('');
    try {
      await api.put(`/dev-mode/projects/${project.id}`, {
        description: `${project.description || ''}\n\n--- NOVA FUNÇÃO ---\n${description.trim()}\n\nItens esperados: ${expectedItems.trim() || 'N/A'}`,
      });
      const res = await api.post<{ tasks: DevTask[] }>(`/dev-mode/projects/${project.id}/generate-tasks`, {});
      setTasks(res.tasks || []);
    } catch (e: any) {
      setErr(e.message || 'Erro ao gerar tarefas');
    } finally {
      setGenerating(false);
    }
  };

  const inputStyle: any = {
    width: '100%', padding: '10px 14px', borderRadius: 10, fontSize: 14,
    background: T.bgCard, border: `1px solid ${theme.border}`,
    color: theme.text, fontFamily: theme.font, outline: 'none', resize: 'vertical' as any,
  };

  const labelStyle: any = {
    display: 'block', fontSize: 12, fontWeight: 600, color: theme.muted,
    marginBottom: 6, textTransform: 'uppercase' as any, letterSpacing: '0.5px',
  };

  return (
    <div style={{ background: theme.card, borderRadius: 16, padding: 28, border: `1px solid ${theme.border}`, maxWidth: 640 }}>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: theme.text, marginBottom: 4 }}>
 Nova Função
      </h3>
      <p style={{ fontSize: 13, color: theme.dimmed, marginBottom: 20 }}>
        Projeto: <strong style={{ color: theme.muted }}>{project.name}</strong>
      </p>

      {err && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: T.dangerBg, color: theme.error, fontSize: 13, marginBottom: 16 }}>
          {err}
        </div>
      )}

      {tasks.length === 0 ? (
        <form onSubmit={handleGenerate}>
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Descreva a nova função *</label>
            <Textarea value={description} onInput={(e: any) => setDescription(e.target.value)}
              placeholder="Ex: Adicionar sistema de pagamento com Stripe, checkout e webhooks..."
              rows={4} style={inputStyle} />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>Itens esperados (opcional)</label>
            <Textarea value={expectedItems} onInput={(e: any) => setExpectedItems(e.target.value)}
              placeholder="Ex: Página de checkout, webhook endpoint, tabela de pagamentos..."
              rows={3} style={inputStyle} />
          </div>

          <div style={{ marginBottom: 24 }}>
            <label style={labelStyle}>Categoria (opcional)</label>
            <Select value={category} onChange={setCategory} placeholder="Sem categoria" options={CATEGORIES.map(c => ({ value: c.key, label: `${c.icon} ${c.label}` }))} />
          </div>

          <div style={{ display: 'flex', gap: 12 }}>
            <Button type="submit" disabled={generating} loading={generating}>
              {generating ? 'Gerando tarefas...' : 'Gerar Passo-a-Passo'}
            </Button>
            <Button variant="ghost" type="button" onClick={onCancel}>
              Cancelar
            </Button>
          </div>
        </form>
      ) : (
        <div>
          <h4 style={{ fontSize: 14, fontWeight: 600, color: theme.success, marginBottom: 16 }}>
 {tasks.length} tarefas geradas
          </h4>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {tasks.map((task, i) => (
              <div key={task.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px',
                borderRadius: 10, background: T.bgCard, border: `1px solid ${theme.border}`,
              }}>
                <span style={{
                  width: 24, height: 24, borderRadius: '50%', background: theme.gradientSubtle,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700, color: theme.primary, flexShrink: 0,
                }}>
                  {i + 1}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>{task.title}</div>
                  {task.description && (
                    <div style={{ fontSize: 12, color: theme.dimmed, marginTop: 4 }}>{task.description}</div>
                  )}
                </div>
                <StatusPill status={task.status} map={TASK_STATUS} />
              </div>
            ))}
          </div>
          <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
            <Button onClick={onDone}>
              Voltar aos Projetos
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══ Schedule Popover ═══ */
function SchedulePopover({ task, projectId, onScheduled }: { task: DevTask; projectId: string; onScheduled: () => void }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'once' | 'recurring'>('once');
  const [dateVal, setDateVal] = useState('');
  const [timeVal, setTimeVal] = useState('');
  const [interval, setInterval] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSchedule = async () => {
    setSaving(true);
    try {
      let scheduled_at: string | null = null;
      let recurrence_interval: string | null = null;

      if (mode === 'once') {
        if (!dateVal || !timeVal) { alert('Selecione data e hora'); setSaving(false); return; }
        scheduled_at = new Date(`${dateVal}T${timeVal}`).toISOString();
      } else {
        if (!interval) { alert('Selecione um intervalo'); setSaving(false); return; }
        recurrence_interval = interval;
        // First run: now + interval
        const map: Record<string, number> = { '1h': 3600000, '6h': 6*3600000, '12h': 12*3600000, '24h': 86400000, '7d': 604800000, '30d': 2592000000 };
        scheduled_at = new Date(Date.now() + (map[interval] || 86400000)).toISOString();
      }

      await api.post(`/dev-mode/projects/${projectId}/tasks/${task.id}/schedule`, {
        scheduled_at,
        recurrence_interval,
      });
      setOpen(false);
      onScheduled();
    } catch (e: any) {
      alert(e.message || 'Erro ao agendar');
    }
    setSaving(false);
  };

  const handleCancel = async () => {
    setSaving(true);
    try {
      await api.post(`/dev-mode/projects/${projectId}/tasks/${task.id}/schedule`, {
        scheduled_at: null,
        recurrence_interval: null,
      });
      setOpen(false);
      onScheduled();
    } catch (e: any) {
      alert(e.message || 'Erro ao cancelar agendamento');
    }
    setSaving(false);
  };

  const isScheduled = !!task.scheduled_at || !!task.recurrence_interval;

  return (
    <div style={{ position: 'relative' }}>
      <Button
        variant="ghost"
        size="xs"
        onClick={() => setOpen(!open)}
        title={isScheduled ? `Agendado: ${task.scheduled_at ? new Date(task.scheduled_at).toLocaleString('pt-BR') : ''}${task.recurrence_interval ? ` (${task.recurrence_interval})` : ''}` : 'Agendar tarefa'}
        style={{
          background: isScheduled ? T.accentBg : undefined,
          color: isScheduled ? theme.indigo : theme.dimmed,
        }}
      >
 {isScheduled ? '' : ''}
      </Button>

      {open && (
        <div style={{
          position: 'absolute', right: 0, top: '100%', marginTop: 6, zIndex: 100,
          background: theme.card, borderRadius: 12, padding: 16, minWidth: 280,
          border: `1px solid ${theme.border}`, boxShadow: `0 8px 32px ${T.blackAlpha40}`,
        }}>
 <div style={{ fontSize: 13, fontWeight: 700, color: theme.text, marginBottom: 12 }}>Agendar Tarefa</div>

          {isScheduled && (
            <div style={{ marginBottom: 12, padding: 8, borderRadius: 8, background: T.accentBg, fontSize: 12, color: theme.indigo }}>
 Agendado para: {task.scheduled_at ? new Date(task.scheduled_at).toLocaleString('pt-BR') : 'N/A'}
              {task.recurrence_interval && <span> • Repete: {task.recurrence_interval}</span>}
            </div>
          )}

          {/* Mode selector */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
            <Button variant={mode === 'once' ? 'primary' : 'ghost'} size="xs" onClick={() => setMode('once')} style={{ flex: 1 }}>
 Uma vez
            </Button>
            <Button variant={mode === 'recurring' ? 'primary' : 'ghost'} size="xs" onClick={() => setMode('recurring')} style={{ flex: 1 }}>
 Recorrente
            </Button>
          </div>

          {mode === 'once' ? (
            <div style={{ display: 'flex', flexDirection: 'column' as any, gap: 8, marginBottom: 12 }}>
              <input type="date" value={dateVal} onInput={(e: any) => setDateVal(e.target.value)} style={{
                padding: '8px 10px', borderRadius: 8, fontSize: 13,
                background: T.bgCard, border: `1px solid ${theme.border}`,
                color: theme.text, fontFamily: theme.font, outline: 'none',
              }} />
              <input type="time" value={timeVal} onInput={(e: any) => setTimeVal(e.target.value)} style={{
                padding: '8px 10px', borderRadius: 8, fontSize: 13,
                background: T.bgCard, border: `1px solid ${theme.border}`,
                color: theme.text, fontFamily: theme.font, outline: 'none',
              }} />
            </div>
          ) : (
            <div style={{ marginBottom: 12 }}>
              <Select value={interval} onChange={setInterval} options={RECURRENCE_OPTIONS.map(o => ({ value: o.value, label: o.label }))} />
            </div>
          )}

          <div style={{ display: 'flex', gap: 6 }}>
            <Button size="sm" onClick={handleSchedule} disabled={saving} loading={saving} style={{ flex: 1 }}>
              {saving ? '...' : 'Agendar'}
            </Button>
            {isScheduled && (
              <Button variant="danger" size="sm" onClick={handleCancel} disabled={saving}>
                Cancelar
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
                          </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══ Project Detail View ═══ */
function ProjectDetail({ projectId, onBack }: { projectId: string; onBack: () => void }) {
  const [project, setProject] = useState<DevProject | null>(null);
  const [tasks, setTasks] = useState<DevTask[]>([]);
  const [jobs, setJobs] = useState<DevJob[]>([]);
  const [loadingDetail, setLoadingDetail] = useState(true);
  const [showNewFunction, setShowNewFunction] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [showPrompt, setShowPrompt] = useState<string | null>(null);
  const [generatingDocs, setGeneratingDocs] = useState(false);
  const [documentation, setDocumentation] = useState<string | null>(null);
  const [confirmExecute, setConfirmExecute] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  const loadProject = useCallback(async () => {
    try {
      const res = await api.get<{ project: DevProject; tasks: DevTask[]; jobs: DevJob[] }>(`/dev-mode/projects/${projectId}`);
      setProject(res.project);
      setTasks(res.tasks || []);
      setJobs(res.jobs || []);
    } catch (_) {}
  }, [projectId]);

  useEffect(() => {
    (async () => {
      await loadProject();
      setLoadingDetail(false);
    })();
  }, [loadProject]);

  // Auto-refresh while tasks are running
  useEffect(() => {
    const hasRunning = tasks.some(t => t.status === 'running');
    if (!hasRunning) return;

    const interval = setInterval(loadProject, 5000);
    return () => clearInterval(interval);
  }, [tasks, loadProject]);

  if (loadingDetail) return (<div style={{ display: 'grid', gap: 12 }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
              <SkeletonBar width="60%" height={16} />
              <div style={{ height: 8 }} />
              <SkeletonBar width="80%" height={12} />
              <div style={{ height: 6 }} />
              <SkeletonBar width="40%" height={12} />
            </div>
          ))}
        </div>);
  if (!project) return <div style={{ color: theme.error, padding: 40 }}>Projeto não encontrado</div>;

  if (showNewFunction) {
    return <NewFunctionForm project={project} onDone={() => { setShowNewFunction(false); loadProject(); }} onCancel={() => setShowNewFunction(false)} />;
  }

  const handleExecuteTask = async (taskId: string) => {
    try {
      await api.post(`/dev-mode/projects/${project.id}/tasks/${taskId}/execute`, {});
      await loadProject();
    } catch (e: any) {
      alert(e.message || 'Erro ao executar task');
    }
  };

  const handleExecuteAll = async () => {
    if (!project.runner_id) {
      alert('Selecione uma máquina primeiro (edite o projeto)');
      return;
    }
    setExecuting(true);
    try {
      const res = await api.post<{ queued: number }>(`/dev-mode/projects/${project.id}/execute-all`, {});
      await loadProject();
    } catch (e: any) {
      alert(e.message || 'Erro ao executar tarefas');
    }
    setExecuting(false);
  };

  const handleGenerateDocs = async () => {
    setGeneratingDocs(true);
    try {
      const res = await api.post<{ documentation: string }>(`/dev-mode/projects/${project.id}/generate-docs`, {});
      setDocumentation(res.documentation || 'Nenhuma documentação gerada.');
    } catch (e: any) {
      alert(e.message || 'Erro ao gerar documentação');
    }
    setGeneratingDocs(false);
  };

  const handleGenerateTasks = async () => {
    setGenerating(true);
    try {
      const res = await api.post<{ tasks: DevTask[] }>(`/dev-mode/projects/${project.id}/generate-tasks`, {});
      setTasks(res.tasks || []);
    } catch (e: any) {
      alert(e.message || 'Erro ao gerar tarefas');
    }
    setGenerating(false);
  };

  const completedCount = tasks.filter(t => t.status === 'completed').length;
  const totalCount = tasks.length;
  const progress = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Button variant="ghost" size="sm" onClick={onBack}>
          ← Voltar
        </Button>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: theme.text, margin: 0 }}>{project.name}</h2>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
            <StatusPill status={project.status} map={STATUS_MAP} />
            {project.runner_id && (
 <span style={{ fontSize: 11, color: theme.dimmed }}>Runner vinculado</span>
            )}
            {project.site_url && (
              <a href={project.site_url} target="_blank" rel="noopener" style={{ fontSize: 12, color: theme.primary }}>
                {project.site_url}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      {totalCount > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 13, color: theme.muted }}>Progresso</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: progress === 100 ? theme.success : theme.primary }}>
              {completedCount}/{totalCount} ({progress}%)
            </span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: T.border }}>
            <div style={{
              height: '100%', borderRadius: 3, transition: 'width 0.3s',
              width: `${progress}%`,
              background: progress === 100 ? theme.success : theme.gradient,
            }} />
          </div>
        </div>
      )}

      {/* Description */}
      {project.description && (
        <div style={{
          background: theme.card, borderRadius: 12, padding: 20, marginBottom: 20,
          border: `1px solid ${theme.border}`, fontSize: 14, color: theme.muted, lineHeight: 1.6,
          whiteSpace: 'pre-wrap' as any, maxHeight: 150, overflowY: 'auto',
        }}>
          {project.description}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap' as any }}>
        <Button onClick={() => setShowNewFunction(true)}>
          Nova Função
        </Button>
        <Button onClick={handleGenerateDocs} disabled={generatingDocs} loading={generatingDocs}
          style={{ background: 'linear-gradient(135deg, #6D4CFF 0%, #9D6CFF 100%)' }}>
          {generatingDocs ? 'Gerando...' : 'Gerar Documentação'}
        </Button>
        {tasks.length === 0 && (
          <Button variant="ghost" onClick={handleGenerateTasks} disabled={generating} loading={generating}>
            {generating ? 'Gerando...' : 'Gerar Tarefas'}
          </Button>
        )}
        {tasks.some(t => t.status === 'pending') && project.runner_id && (
          <Button variant="success" onClick={handleExecuteAll} disabled={executing} loading={executing}>
            {executing ? 'Enviando...' : `Executar Tudo (${tasks.filter(t => t.status === 'pending').length})`}
          </Button>
        )}
      </div>

      {/* Tasks */}
      {tasks.length > 0 && (() => {
        const filteredTasks = categoryFilter
          ? tasks.filter(t => t.category === categoryFilter)
          : tasks;
        const categoryCounts = CATEGORIES.map(c => ({
          ...c,
          count: tasks.filter(t => t.category === c.key).length,
        }));

        return (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginBottom: 12 }}>
 Tarefas ({tasks.length})
          </h3>

          {/* Category filter tabs */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' as any }}>
            <Button variant={!categoryFilter ? 'primary' : 'ghost'} size="xs" onClick={() => setCategoryFilter('')}>
              Todas ({tasks.length})
            </Button>
            {categoryCounts.filter(c => c.count > 0).map(c => (
              <Button key={c.key} variant={categoryFilter === c.key ? 'primary' : 'ghost'} size="xs" onClick={() => setCategoryFilter(categoryFilter === c.key ? '' : c.key)} style={{ color: categoryFilter === c.key ? c.color : undefined }}>
                {c.icon} {c.label} ({c.count})
              </Button>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filteredTasks.sort((a, b) => a.step_number - b.step_number).map((task) => (
              <div key={task.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px',
                borderRadius: 12, background: theme.card, border: `1px solid ${theme.border}`,
              }}>
                <span style={{
                  width: 28, height: 28, borderRadius: '50%', background: theme.gradientSubtle,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, color: theme.primary, flexShrink: 0,
                }}>
                  {task.step_number}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>{task.title}</div>
                  {task.description && (
                    <div style={{ fontSize: 12, color: theme.dimmed, marginTop: 4 }}>{task.description}</div>
                  )}
                  {task.output && (
                    <div style={{
                      fontSize: 12, color: theme.success, marginTop: 6, fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap' as any, maxHeight: 100, overflowY: 'auto',
                      background: T.successBg, padding: 8, borderRadius: 6,
                    }}>
                      {task.output.slice(0, 500)}{task.output.length > 500 ? '...' : ''}
                    </div>
                  )}
                  {task.error && (
                    <div style={{
                      fontSize: 12, color: theme.error, marginTop: 6, fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap' as any, maxHeight: 100, overflowY: 'auto',
                      background: T.dangerBg, padding: 8, borderRadius: 6,
                    }}>
                      {task.error.slice(0, 500)}
                    </div>
                  )}
                  {task.duration_ms && (
                    <div style={{ fontSize: 11, color: theme.dimmed, marginTop: 4 }}>
 {Math.round(task.duration_ms / 1000)}s
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, flexDirection: 'column' as any }}>
                  <StatusPill status={task.status} map={TASK_STATUS} />
                  <CategoryPill category={task.category} />
                  {task.scheduled_at && (
                    <div style={{ fontSize: 10, color: theme.indigo, marginTop: 2 }}>
 {new Date(task.scheduled_at).toLocaleString('pt-BR')}
 {task.recurrence_interval && ` • ${task.recurrence_interval}`}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    {(task.status === 'pending' || task.status === 'scheduled') && project.runner_id && (
                      <Button size="xs" onClick={() => setConfirmExecute(task.id)}>
                                              </Button>
                    )}
                    {(task.status === 'pending' || task.status === 'scheduled') && project.runner_id && task.prompt && (
                      <SchedulePopover task={task} projectId={project.id} onScheduled={loadProject} />
                    )}
                    {task.prompt && (
                      <Button variant="ghost" size="xs" onClick={() => setShowPrompt(showPrompt === task.id ? null : task.id)}>
                        
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Prompt detail popup */}
          {showPrompt && (() => {
            const task = tasks.find(t => t.id === showPrompt);
            if (!task?.prompt) return null;
            return (
              <div style={{
                marginTop: 12, background: T.bgOverlay, borderRadius: 12, padding: 16,
                border: `1px solid ${theme.border}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: theme.muted }}>
                    Prompt — Step {task.step_number}: {task.title}
                  </span>
                  <Button variant="ghost" size="xs" onClick={() => setShowPrompt(null)}></Button>
                </div>
                <pre style={{
                  fontSize: 12, color: theme.text, fontFamily: 'monospace', whiteSpace: 'pre-wrap' as any,
                  maxHeight: 200, overflowY: 'auto', lineHeight: 1.6,
                }}>
                  {task.prompt}
                </pre>
              </div>
            );
          })()}
        </div>
        );
      })()}

      {/* Documentation */}
      {documentation && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: theme.text, margin: 0 }}>
 Documentação
            </h3>
            <Button variant="ghost" size="sm" onClick={() => setDocumentation(null)}>Fechar</Button>
          </div>
          <div style={{
            background: theme.card, borderRadius: 12, padding: 20, border: `1px solid ${theme.border}`,
            fontSize: 14, color: theme.text, lineHeight: 1.7, whiteSpace: 'pre-wrap' as any,
            maxHeight: 500, overflowY: 'auto', fontFamily: 'monospace',
          }}>
            {documentation}
          </div>
        </div>
      )}

      {/* Activity Log */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: theme.text, marginBottom: 12 }}>
 Atividade
        </h3>
        <ActivityLog projectId={projectId} />
      </div>

      {confirmExecute && (() => {
        const confirmTask = tasks.find(t => t.id === confirmExecute);
        if (!confirmTask) return null;

        return (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: T.bgOverlay, display: 'flex', alignItems: 'center',
            justifyContent: 'center', zIndex: 1000,
          }} onClick={() => setConfirmExecute(null)}>
            <div style={{
              background: theme.card, borderRadius: 16, padding: 24, maxWidth: 420, width: '90%',
              border: `1px solid ${theme.border}`, boxShadow: `0 20px 60px ${T.blackAlpha40}`,
            }} onClick={(e) => e.stopPropagation()}>
              <h3 style={{ fontSize: 16, fontWeight: 700, color: theme.text, marginBottom: 16 }}>
 Confirmar Execução
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20, fontSize: 13 }}>
 <div><span style={{color: theme.dimmed}}>Projeto:</span> <strong style={{color: theme.text}}>{project.name}</strong></div>
 <div><span style={{color: theme.dimmed}}>Step:</span> <strong style={{color: theme.text}}>{confirmTask.step_number} — {confirmTask.title}</strong></div>
 {confirmTask.description && <div><span style={{color: theme.dimmed}}>Descrição:</span> <strong style={{color: theme.text}}>{confirmTask.description}</strong></div>}
 <div><span style={{color: theme.dimmed}}>Criado:</span> <strong style={{color: theme.text}}>{new Date(confirmTask.created_at).toLocaleString('pt-BR')}</strong></div>
 <div><span style={{color: theme.dimmed}}>Status:</span> <strong style={{color: theme.text}}>{TASK_STATUS[confirmTask.status]?.label || confirmTask.status}</strong></div>
 {confirmTask.duration_ms && <div><span style={{color: theme.dimmed}}>Duração anterior:</span> <strong style={{color: theme.text}}>{Math.round(confirmTask.duration_ms/1000)}s</strong></div>}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <Button variant="success" onClick={() => {
                  setConfirmExecute(null);
                  handleExecuteTask(confirmTask.id);
                }} style={{ flex: 1 }}>
                  Executar agora
                </Button>
                <Button variant="ghost" onClick={() => setConfirmExecute(null)} style={{ flex: 1 }}>
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ═══ Main Page ═══ */
export function DevModePage() {
  // if (!isAdmin.value && !hasActivePlan.value) return <NoPlanGate />;
  const [view, setView] = useState<'list' | 'create' | 'detail'>('list');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  useEffect(() => { fetchProjects(); }, []);

  const openProject = (id: string) => {
    setSelectedProjectId(id);
    setView('detail');
  };

  if (view === 'create') {
    return (
      <div style={{ padding: '32px 24px', maxWidth: 900, margin: '0 auto', fontFamily: theme.font }}>
        <CreateProjectForm
          onCreated={() => { fetchProjects(); setView('list'); }}
          onCancel={() => setView('list')}
        />
      </div>
    );
  }

  if (view === 'detail' && selectedProjectId) {
    return (
      <div style={{ padding: '32px 24px', maxWidth: 900, margin: '0 auto', fontFamily: theme.font }}>
        <ProjectDetail projectId={selectedProjectId} onBack={() => { fetchProjects(); setView('list'); }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '32px 24px', maxWidth: 900, margin: '0 auto', fontFamily: theme.font }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <h1 style={{ fontSize: 24, fontWeight: 800, color: theme.text, margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
 <span style={{ fontSize: 28 }}></span>Modo Dev
        </h1>
        <p style={{ fontSize: 14, color: theme.dimmed, marginTop: 6 }}>
          Crie projetos do zero ou adicione novas funções. A IA gera o passo-a-passo e executa na sua máquina.
        </p>
      </div>

      {/* Choice Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16, marginBottom: 32 }}>
        <div
          onClick={() => setView('create')}
          style={{
            background: theme.gradientSubtle, borderRadius: 16, padding: 24,
            border: `1px solid ${T.primary}`, cursor: 'pointer',
            transition: 'all 0.2s',
          }}
          onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.primary; }}
          onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.primary; }}
        >
 <div style={{ fontSize: 32, marginBottom: 12 }}></div>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.text, marginBottom: 6 }}>Projeto do Zero</div>
          <div style={{ fontSize: 13, color: theme.muted }}>Comece um novo projeto com descrição, docs e arquivos do sistema.</div>
        </div>

        {projects.value.length > 0 && (
          <div
            onClick={() => {
              if (projects.value.length === 1) openProject(projects.value[0]!.id);
            }}
            style={{
              background: T.accentBg, borderRadius: 16, padding: 24,
              border: `1px solid ${T.accentBg}`, cursor: projects.value.length === 1 ? 'pointer' : 'default',
              transition: 'all 0.2s',
            }}
          >
 <div style={{ fontSize: 32, marginBottom: 12 }}></div>
            <div style={{ fontSize: 16, fontWeight: 700, color: theme.text, marginBottom: 6 }}>Nova Função</div>
            <div style={{ fontSize: 13, color: theme.muted }}>Adicione uma nova funcionalidade a um projeto existente.</div>
          </div>
        )}

        <a
          href="/security-scan"
          style={{
            background: T.successBg, borderRadius: 16, padding: 24,
            border: `1px solid ${T.success}`, cursor: 'pointer',
            transition: 'all 0.2s', textDecoration: 'none', display: 'block',
          }}
          onMouseEnter={(e: any) => { e.currentTarget.style.borderColor = T.success; }}
          onMouseLeave={(e: any) => { e.currentTarget.style.borderColor = T.success; }}
        >
 <div style={{ fontSize: 32, marginBottom: 12 }}></div>
          <div style={{ fontSize: 16, fontWeight: 700, color: theme.text, marginBottom: 6 }}>Teste de Segurança</div>
          <div style={{ fontSize: 13, color: theme.muted }}>Analise a segurança de qualquer site em segundos.</div>
        </a>
      </div>

      {/* Projects List */}
      {loading.value ? (
        <div style={{ display: 'grid', gap: 12 }}>
          {[1,2,3].map(i => (
            <div key={i} style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 }}>
              <SkeletonBar width="60%" height={16} />
              <div style={{ height: 8 }} />
              <SkeletonBar width="80%" height={12} />
              <div style={{ height: 6 }} />
              <SkeletonBar width="40%" height={12} />
            </div>
          ))}
        </div>
      ) : error.value ? (
        <div style={{ padding: 20, borderRadius: 12, background: T.dangerBg, color: theme.error, fontSize: 14 }}>
          {error.value}
        </div>
      ) : projects.value.length === 0 ? (
        <div style={{
          textAlign: 'center' as any, padding: 48, borderRadius: 16,
          background: theme.card, border: `1px solid ${theme.border}`,
        }}>
 <div style={{ fontSize: 48, marginBottom: 16 }}></div>
          <div style={{ fontSize: 16, fontWeight: 600, color: theme.text, marginBottom: 8 }}>Nenhum projeto ainda</div>
          <div style={{ fontSize: 14, color: theme.dimmed }}>Clique em "Projeto do Zero" para começar.</div>
        </div>
      ) : (
        <div>
          <h2 style={{ fontSize: 16, fontWeight: 700, color: theme.text, marginBottom: 14 }}>
            Seus Projetos ({projects.value.length})
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 14 }}>
            {projects.value.map((p) => (
              <div
                key={p.id}
                onClick={() => openProject(p.id)}
                style={{
                  background: theme.card, borderRadius: 14, padding: 20,
                  border: `1px solid ${theme.border}`, cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e: any) => { e.currentTarget.style.background = theme.cardHover; e.currentTarget.style.borderColor = T.primaryBg; }}
                onMouseLeave={(e: any) => { e.currentTarget.style.background = theme.card; e.currentTarget.style.borderColor = theme.border; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: theme.text }}>{p.name}</div>
                  <StatusPill status={p.status} map={STATUS_MAP} />
                </div>
                {p.description && (
                  <div style={{
                    fontSize: 13, color: theme.dimmed, lineHeight: 1.5,
                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' as any,
                    overflow: 'hidden',
                  }}>
                    {p.description}
                  </div>
                )}
                <div style={{ fontSize: 11, color: theme.dimmed, marginTop: 10 }}>
                  {new Date(p.created_at).toLocaleDateString('pt-BR')}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
